import React, { useEffect, useRef, useState } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';
import ScrambledText from '../scrambledtext';

const StyledAboutSection = styled.section`
  max-width: 900px;
  min-height: 100vh;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;

const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 40px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;

const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(80%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const BIRTH_YEAR = 1992;
const BIRTH_MONTH = 1;
const BIRTH_DAY = 15;

function calcAge(at = new Date()) {
  const y = at.getFullYear();
  const m = at.getMonth() + 1; // 1-12
  const d = at.getDate();

  let age = y - BIRTH_YEAR;
  const birthdayPassed =
    m > BIRTH_MONTH || (m === BIRTH_MONTH && d >= BIRTH_DAY);
  if (!birthdayPassed) age--;
  return age;
}

function nextBirthday(from = new Date()) {
  const year = from.getFullYear();
  const candidate = new Date(year, BIRTH_MONTH - 1, BIRTH_DAY, 0, 0, 0, 0);
  return candidate > from
    ? candidate
    : new Date(year + 1, BIRTH_MONTH - 1, BIRTH_DAY, 0, 0, 0, 0);
}

const MS_MAX_TIMEOUT = 2147483646; // max veilige setTimeout delay (~24.8 dagen)

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [age, setAge] = useState(null); // client-only om SSR mismatch te voorkomen
  const ageTimerRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion) return;
    sr.reveal(revealContainer.current, srConfig());
  }, [prefersReducedMotion]);

  // Leeftijd instellen en automatisch updaten
  useEffect(() => {
    // Bereken initieel na mount (client-side)
    setAge(calcAge());

    const schedule = () => {
      const now = new Date();

      // doel 1: exact op volgende verjaardag (00:00)
      const target = nextBirthday(now);
      const msToBirthday = target.getTime() - now.getTime();

      // doel 2: volgende lokale middernacht, om lange delays te chunk-en
      const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
      const msToMidnight = midnight.getTime() - now.getTime();

      // Kies veiligste korte delay om browsers te ontzien
      const delay = Math.min(msToBirthday, MS_MAX_TIMEOUT, msToMidnight);

      ageTimerRef.current = setTimeout(() => {
        setAge(calcAge()); // herbereken
        schedule();        // plan de volgende cyclus
      }, delay);
    };

    schedule();

    return () => {
      if (ageTimerRef.current) clearTimeout(ageTimerRef.current);
    };
  }, []);

  const skills = ['Marketing', 'CRM', 'Growth', 'Web development', 'Content', 'Data & attributie'];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">Over mij</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              {age !== null ? `Ik ben ${age} jaar oud, ` : ''}
              marketeer met een passie voor innovatie, groei en performance. Geboren en getogen in Amstelveen.
            </p>

            <p>
              Welkom bij mijn portfolio. Ik begon mijn reis als consultant bij een{' '}
              <a href="https://imlounge.nl" target="_blank" rel="noreferrer">mediabureau</a>, werd performance marketeer voor{' '}
              <a href="https://beyonddutch.nl" target="_blank" rel="noreferrer">een internationale scale-up</a> en was CMO van een{' '}
              <a href="https://bikefair.org" target="_blank" rel="noreferrer">digitale marktplaats</a>.
            </p>

            <p>
              Vandaag help ik merken via mijn eigen bureau{' '}
              <a href="https://flii.nl" target="_blank" rel="noreferrer">Flii Media</a>, samen met een vertrouwd netwerk aan freelancers.
            </p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => (
              <li key={i}><ScrambledText text={skill} /></li>
            ))}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <a href="https://linkedin.com/in/sten-bossong" target="_blank" rel="noopener noreferrer">
              <StaticImage
                className="img"
                src="../../images/me.png"
                width={500}
                quality={95}
                formats={['AUTO', 'WEBP', 'AVIF']}
                alt="Headshot"
              />
            </a>
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
